import * as yup from 'yup';

import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useTheme
} from "@mui/material";
import React, { useEffect, useState } from "react";
import {
  useAddNewCVMutation,
  useGetCandidateCvsQuery,
} from "../../lib/cvsSlice";

import AddCircleIcon from "@mui/icons-material/AddCircle";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import DownloadIcon from "@mui/icons-material/Download";
import EditIcon from "@mui/icons-material/Edit";
import PrintIcon from "@mui/icons-material/Print";
import AddIcon from "@mui/icons-material/Add";
import ActionCard from "./ActionCard";
import TokenExpiredModal from "../../components/TokenExpiredModal";
import UploadPDF from "../../components/UploadCVExtractDataWithAiApp";
import api from "../../api/axiosInstance";
import { primaryRoles } from "../../config";
import {useForm} from "react-hook-form";
import { useNavigate } from "react-router";
import { useProfile } from "../../providers/ProfileProvider";
import { yupResolver } from "@hookform/resolvers/yup";

const cvHeaderSchema = yup.object().shape({
  mainRole: yup.string().required("CV Role is required"),
});

const ActionButtons = ({ cv, navigate }) => (
  <Box sx={{ display: 'flex', gap: '12px' }}>
    <IconButton size="small" data-icon-style="action" title="Download CV">
      <DownloadIcon fontSize="small" />
    </IconButton>
    <IconButton size="small" data-icon-style="action" title="Print CV">
      <PrintIcon fontSize="small" />
    </IconButton>
    <IconButton
      size="small"
      data-icon-style="action"
      onClick={() => navigate(`/cv/${cv._id}`)}
      title="Edit CV"
    >
      <EditIcon fontSize="small" />
    </IconButton>
    <IconButton size="small" data-icon-style="action" title="Delete CV">
      <DeleteIcon fontSize="small" />
    </IconButton>
  </Box>
);

const CreateCVDialog = ({ open, onClose, onSubmit, allCvs, errors, control }) => {
  const [selectedRole, setSelectedRole] = useState('');
  const [customRole, setCustomRole] = useState('');
  const [copyCvId, setCopyCvId] = useState(null);
  const [createFromPersonal, setCreateFromPersonal] = useState(false);
  const theme = useTheme();

  const roles = [...primaryRoles];

  const handleRoleSelect = (role) => {
    setSelectedRole(role);
    if (role !== 'Other') {
      setCustomRole('');
    }
  };

  const handleClose = () => {
    setSelectedRole('');
    setCustomRole('');
    setCopyCvId(null);
    setCreateFromPersonal(false);
    onClose();
  };

  const handleCreate = () => {
    const roleValue = selectedRole === 'Other' ? customRole : selectedRole;
    onSubmit({ mainRole: roleValue, copyCvId, createFromPersonal });
  };

  if (!open) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      fontFamily: theme.typography.fontFamily
    }}>
      <div style={{
        backgroundColor: theme.palette.background.paper,
        borderRadius: '16px',
        width: '90%',
        maxWidth: '600px',
        maxHeight: '90vh',
        overflow: 'auto',
        boxShadow: '0 20px 40px rgba(0, 0, 0, 0.15)'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '24px 24px 16px 24px',
          borderBottom: 'none'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <AddIcon fontSize="small" />
            <Typography variant="dialogTitle">
              Create New CV
            </Typography>
          </div>
          <IconButton data-icon-style="close" onClick={handleClose} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </div>

        <div style={{ padding: '0 24px 24px 24px' }}>
          <div style={{ marginBottom: '32px' }}>
            <p style={{ 
              margin: '0 0 16px 0', 
              fontSize: '16px', 
              fontWeight: '500',
              color: theme.palette.text.secondary,
              fontFamily: theme.typography.fontFamily
            }}>
              Select Role for CV.
            </p>
            
            <div style={{ 
              display: 'flex', 
              flexWrap: 'wrap', 
              gap: '8px',
              marginBottom: '24px'
            }}>
              {roles.map((role) => (
                <Button
                  key={role}
                  data-selectable
                  data-size="compact"
                  data-selected={selectedRole === role}
                  onClick={() => handleRoleSelect(role)}
                  disableRipple
                >
                  {role}
                </Button>
              ))}
            </div>

            {selectedRole === 'Other' && (
              <Box sx={{ marginBottom: '24px' }}>
                <Typography variant="dialogLabel" component="label" htmlFor="other-role-input">
                  Other Role
                </Typography>
                <TextField
                  id="other-role-input"
                  data-dialog-input
                  variant="outlined"
                  placeholder="Enter Role"
                  value={customRole}
                  onChange={(e) => setCustomRole(e.target.value)}
                  fullWidth
                />
              </Box>
            )}
          </div>

          {allCvs.length > 0 && (
            <div style={{ marginBottom: '24px' }}>
              <Button
                data-selectable
                data-size="full"
                data-selected={copyCvId !== null}
                onClick={() => setCopyCvId(copyCvId !== null ? null : "")}
                disableRipple
                sx={{ marginBottom: '12px' }}
              >
                DUPLICATE AN EXISTING CV
              </Button>

              {copyCvId !== null && (
                <Box sx={{ mb: 1.5 }}>
                  <Select
                    value={copyCvId}
                    onChange={(e) => setCopyCvId(e.target.value)}
                    displayEmpty
                    fullWidth
                    size="small"
                  >
                    <MenuItem value="">Select CV to copy</MenuItem>
                    {allCvs.map((cv) => (
                      <MenuItem key={cv._id} value={cv._id}>
                        {cv.mainRole}
                      </MenuItem>
                    ))}
                  </Select>
                </Box>
              )}

              <Button
                data-selectable
                data-size="full"
                data-selected={createFromPersonal}
                onClick={() => setCreateFromPersonal(!createFromPersonal)}
                disableRipple
              >
                CREATE A "CLEAN CV" FROM ZERO
              </Button>

              {createFromPersonal && (
                <div style={{ marginTop: '16px' }}>
                  <UploadPDF handleClose={handleClose} />
                </div>
              )}

              <Typography variant="dialogHelperText" display="block" sx={{ mt: 2 }}>
                Starting from a blank CV will require you to enter your information again, even if you've already created a CV with us.
              </Typography>
            </div>
          )}

          <Box sx={{ display: 'flex', gap: '12px', justifyContent: 'space-between' }}>
            <Button data-dialog-action data-action-type="cancel" onClick={handleClose} disableRipple>
              CANCEL
            </Button>
            <Button
              data-dialog-action
              data-action-type="create"
              onClick={handleCreate}
              disabled={!selectedRole || (selectedRole === 'Other' && !customRole.trim())}
              disableRipple
            >
              CREATE
            </Button>
          </Box>
        </div>
      </div>
    </div>
  );
};

const generateCvId = (index) => {
  const baseNumber = 25000000;
  const uniqueId = baseNumber + index + 1;
  return `NC-${uniqueId}`;
};

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-CA'); 
};

const getTimeAgo = (dateString) => {
  if (!dateString) return 'N/A';
  
  const now = new Date();
  const updatedDate = new Date(dateString);
  const diffInMs = now - updatedDate;
  
  const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
  const diffInMonths = Math.floor(diffInDays / 30);
  const diffInYears = Math.floor(diffInMonths / 12);
  
  if (diffInYears > 0) {
    const remainingMonths = diffInMonths % 12;
    if (remainingMonths > 0) {
      return `${diffInYears} year${diffInYears > 1 ? 's' : ''} and ${remainingMonths} month${remainingMonths > 1 ? 's' : ''} ago`;
    }
    return `${diffInYears} year${diffInYears > 1 ? 's' : ''} ago`;
  }
  
  if (diffInMonths > 0) {
    return `${diffInMonths} month${diffInMonths > 1 ? 's' : ''} ago`;
  }
  
  if (diffInDays > 0) {
    return `${diffInDays} day${diffInDays > 1 ? 's' : ''} ago`;
  }
  
  return 'Today';
};

const CvDashBoard = () => {
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const profile = useProfile();
  const navigate = useNavigate();

  // Use Redux query with profile ID
  const { 
    data: cvsData, 
    isLoading, 
    isSuccess,
    isError,
    error 
  } = useGetCandidateCvsQuery(profile?._id, {
    skip: !profile?._id
  });

  const [addNewCV] = useAddNewCVMutation();
  const [allCvs, setAllCvs] = useState([]);
  const [allCvTitles, setAllCvTitles] = useState([]);
  const [tokenExpiredModalOpen, setTokenExpiredModalOpen] = useState(false);
  const [searchName, setSearchName] = useState("");
  const [presentedToCustomer, setPresentedToCustomer] = useState("All");
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  // Sync Redux data to local state
  useEffect(() => {
    if (isSuccess && cvsData) {
      const cvArray = Object.values(cvsData.entities || {});
      setAllCvs(cvArray);
      setAllCvTitles(cvArray.map(cv => cv.mainRole));
    }
  }, [isSuccess, cvsData]);

  useEffect(() => {
    if (isError) {
      console.error('Error fetching CVs:', error);
      if (error?.status === 401) {
        setTokenExpiredModalOpen(true);
      }
    }
  }, [isError, error]);


  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    reset();
  };

  const {
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(cvHeaderSchema),
    defaultValues: {
      mainRole: "",
    },
  });

  const submitResults = async (formData) => {
    const { mainRole, copyCvId: cvIdToCopy, createFromPersonal: fromPersonal } = formData;

    let cvData = {};

    if (cvIdToCopy) {
      try {
        const response = await api.get(`cv/${cvIdToCopy}`);

        cvData = {
          languages: response.data.languages || [{}],
          educations: response.data.educations || [{}],
          skills: response.data.skills || [{}],
          achievements: response.data.achievements || [],
          certifications: response.data.certifications || [{}],
          projects: response.data.projects || [{}],
          courses: response.data.courses || [{}],
          gitRepo: response.data.gitRepo || "",
          portfolio: response.data.portfolio || "",
          summary: response.data.summary || "",
          techSkills: response.data.techSkills || [{}],
          techExperiences: response.data.techExperiences || [{}],
        };
      } catch (error) {
        console.error(error);
      }
    }

    const requestData = {
      mainRole,
      ...(cvIdToCopy ? cvData : {}),
      ...formData,
    };

    try {
      const { data: responseData } = await addNewCV(requestData);
      setOpen(false);
      reset();
      navigate(`/cv/${responseData._id}`);
    } catch (error) {
      console.error(error.message);
    }
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const filteredData = allCvs.filter(cv => {
    const matchesSearch = cv.mainRole && cv.mainRole.toLowerCase().includes(searchName.toLowerCase());
    const matchesFilter = presentedToCustomer === "All" || presentedToCustomer === "No";
    return matchesSearch && matchesFilter;
  });

  const totalItems = filteredData.length;
  const totalPages = Math.ceil(totalItems / rowsPerPage);
  const startIndex = (page - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const paginatedData = filteredData.slice(startIndex, endIndex);

  const displayStart = totalItems === 0 ? 0 : startIndex + 1;
  const displayEnd = Math.min(endIndex, totalItems);

  return (
    <div>
      <TokenExpiredModal
        isOpen={tokenExpiredModalOpen}
        onClose={() => setTokenExpiredModalOpen(false)}
      />

      <div
        style={{
          background: theme.palette.background.neutral,
          minHeight: '100%',
          position: 'relative',
        }}
      >
        {isLoading && (
          <div style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)'
          }}>
            <CircularProgress />
          </div>
        )}
        
        <Typography
          variant="h3"
          fontWeight="bold"
          sx={{
            mb: 1,
            fontSize: '24px',
            color: '#000000',
            lineHeight: '2rem',
          }}
        >
          CVs
        </Typography>

        <Typography
          variant="h6"
          color="text.secondary"
          sx={{
            fontWeight: 'normal',
            fontSize: '14px',
            marginBottom: '24px',
          }}
        >
          Manage your CVs from here.
        </Typography>
        
        <div style={{ position: 'relative', height: '152px', marginBottom: '24px' }}>
          <div style={{ 
            position: 'absolute', 
            top: 0, 
            left: 0, 
            width: 'calc((100vw - 346px) / 2)',
            height: '152px' 
          }}>
            <ActionCard
              icon={AddIcon}
              title="Create New CV"
              description="Start building a new CV from scratch"
              buttonText="Create CV"
              onClick={handleClickOpen}
              iconColor={theme.palette.primary.main}
              hoverable
            />
          </div>

          <div style={{ 
            position: 'absolute', 
            top: 0, 
            left: 'calc((100vw - 346px) / 2 + 16px)', 
            width: 'calc((100vw - 346px) / 2)',
            height: '152px' 
          }}>
            <ActionCard
              icon={EditIcon}
              title="Complete Your CV"
              description='Your "Marketing Manager" CV is 75% complete'
              buttonText="Complete"
              progress={75}
              iconColor="#7b68ee"
            />
          </div>
        </div>

        <Box sx={{ 
          display: 'flex', 
          gap: '16px', 
          marginBottom: '24px',
          alignItems: 'center'
        }}>
        </Box>

        <TableContainer 
          component={Paper} 
          sx={{ 
            boxShadow: 'none', 
            border: `1px solid ${theme.palette.grey[50]}`
          }}
        >
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>CV Role</TableCell>
                <TableCell>CV ID No</TableCell>
                <TableCell>Created Date</TableCell>
                <TableCell>Last Updated</TableCell>
                <TableCell>CV Quality Index</TableCell>
                <TableCell>Actions</TableCell>
                <TableCell>Presented to Customer</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedData.map((cv, index) => {
                const actualIndex = startIndex + index;
                
                return (
                  <TableRow key={cv._id || index} sx={{ height: '48px' }}>
                    <TableCell>{cv.mainRole}</TableCell>
                    <TableCell>{cv.cvId || generateCvId(actualIndex)}</TableCell>
                    <TableCell>{formatDate(cv.createdAt || cv.createdDate)}</TableCell>
                    <TableCell>{getTimeAgo(cv.updatedAt || cv.lastUpdated)}</TableCell>
                    <TableCell>{cv.qualityIndex || 85}%</TableCell>
                    <TableCell>
                      <ActionButtons cv={cv} navigate={navigate} />
                    </TableCell>
                    <TableCell>No</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'flex-end', 
          alignItems: 'center',
          marginTop: '16px',
          height: '40px',
          gap: '16px'
        }}>
          <Typography variant="body2">
            Rows per page: {rowsPerPage}
          </Typography>
          <Typography variant="body2">
            {displayStart}-{displayEnd} of {totalItems}
          </Typography>
          <Pagination 
            count={totalPages} 
            page={page} 
            onChange={handlePageChange}
            size="small"
          />
        </Box>
      </div>

      <CreateCVDialog
        open={open}
        onClose={handleClose}
        onSubmit={submitResults}
        allCvs={allCvs}
        errors={errors}
        control={control}
      />
    </div>
  );
};

export default CvDashBoard;
